/**
 * Desktop Sidebar Component
 * Collapsible sidebar for desktop/tablet views
 */

import { useTranslation } from '@ufc/core'
import { isRtlLanguage } from '@ufc/portal-shared'
import { useSidebar } from '@/providers'
import { cn, PanelLeftClose, PanelLeft } from '@ufc/uikit'
import { SIDEBAR_DIMENSIONS } from '../constants'
import { SidebarContent } from './sidebar-content'

// ============================================
// Helper Functions
// ============================================

function getSidebarStyles(collapsed: boolean): string {
  return cn(
    'glass-strong flex flex-col m-4 rounded-2xl relative transition-[width] duration-300 ease-in-out flex-shrink-0',
    collapsed ? SIDEBAR_DIMENSIONS.COLLAPSED_WIDTH : SIDEBAR_DIMENSIONS.EXPANDED_WIDTH
  )
}

function getToggleButtonStyles(isRTL: boolean): string {
  return cn(
    'absolute top-1/2 -translate-y-1/2 w-6 h-12 bg-card border border-border rounded-full flex items-center justify-center shadow-md hover:bg-secondary transition-colors duration-200 z-20',
    isRTL ? '-start-3' : '-end-3'
  )
}

function getToggleIcon(collapsed: boolean, isRTL: boolean): typeof PanelLeft {
  if (collapsed) {
    return isRTL ? PanelLeftClose : PanelLeft
  }
  return isRTL ? PanelLeft : PanelLeftClose
}

function getToggleLabel(collapsed: boolean): string {
  return collapsed ? 'Expand sidebar' : 'Collapse sidebar'
}

// ============================================
// Component
// ============================================

export function DesktopSidebar() {
  const { state, toggle, isMobile } = useSidebar()
  const { i18n } = useTranslation()
  const isRTL = isRtlLanguage(i18n.language)
  const collapsed = state === 'collapsed'

  if (isMobile) return null

  const ToggleIcon = getToggleIcon(collapsed, isRTL)

  return (
    <aside className={getSidebarStyles(collapsed)}>
      <SidebarContent collapsed={collapsed} />

      <button
        onClick={toggle}
        className={getToggleButtonStyles(isRTL)}
        aria-label={getToggleLabel(collapsed)}
      >
        <ToggleIcon className="w-3.5 h-3.5 text-muted-foreground" />
      </button>
    </aside>
  )
}
